import { useState, useContext } from 'react'
import { useMutation } from '@apollo/client'
import { LotContext } from '../../../context/LotContext/lot.context'
import { CheckoutContext } from '../../../context/CheckoutContext/checkout.context'
import { CREATE_BOOKING_DAILY } from '../../../graphql/mutation/createBookingDaily.mutation'
import { CREATE_BOOKING_HOURLY } from '../../../graphql/mutation/createBookingHourly.mutation'
import { BookingCreateBookingHourlyMutation, BookingCreateBookingMutation } from 'parkdepot-shared/gql/graphql'
import { useAuthenticatedMutation, useIsLoggedIn } from '../../../hooks/useAuth'
import { CREATE_USER_BOOKING_DAILY } from '../../../graphql/mutation/createUserBookingDaily.mutation'
import { CREATE_USER_BOOKING_HOURLY } from '../../../graphql/mutation/createUserBookingHourly.mutation'
import { CREATE_BOOKING_NIGHTLY } from '../../../graphql/mutation/createBookingNightly.mutation'
import { LotViewName } from '../../../types/lot.interface'

export const useBookingManagement = () => {
    const { lot } = useContext(LotContext)
    const { checkout, modifyAndSetCheckout } = useContext(CheckoutContext)
    const isLoggedIn = useIsLoggedIn()

    const [successfulBooking, setSuccessfulBooking] = useState<boolean>(false)
    const [bookingId, setBookingId] = useState<number | null>(null)
    const [referenceNumber, setReferenceNumber] = useState<string>('')
    const [orderDataDaily, setOrderDataDaily] = useState<BookingCreateBookingMutation | null>(null)
    const [orderDataHourly, setOrderDataHourly] = useState<BookingCreateBookingHourlyMutation | null>(null)
    const [unavailableDates, setUnavailableDates] = useState<string[]>([])

    const clearAllBookingData = () => {
        if (bookingId) {
            setBookingId(null)
        }
        if (orderDataDaily) {
            setOrderDataDaily(null)
        }
        if (orderDataHourly) {
            setOrderDataHourly(null)
        }
    }

    const onBookingError = () => {
        clearAllBookingData()
    }

    const [createUserBookingDaily] = useAuthenticatedMutation({
        mutation: CREATE_USER_BOOKING_DAILY,
        onCompleted: (data) => {
            setOrderDataDaily(data)
            setBookingId(data.booking_create_user_booking.id)
            setSuccessfulBooking(data.booking_create_user_booking.success)
            // removing duplicates
            const unavailableDatesFromApi: (string | null)[] =
                data?.booking_create_user_booking?.unavailable_dates ?? []
            setUnavailableDates([...new Set(unavailableDatesFromApi as string[])])
            setReferenceNumber(data?.booking_create_user_booking?.booking_reference ?? '')
        },
        onError: () => {
            // TODO: handle or return error
        }
    })

    const [createUserBookingHourly] = useAuthenticatedMutation({
        mutation: CREATE_USER_BOOKING_HOURLY,
        onCompleted: (data) => {
            setOrderDataHourly(data)
            setReferenceNumber(data?.booking_create_user_booking_hourly?.booking_reference ?? '')
            setBookingId(data.booking_create_user_booking_hourly.id)
            setSuccessfulBooking(data.booking_create_user_booking_hourly.success)
        },
        onError: (error) => {
            // TODO: handle or return error
        }
    })

    const [createUserBookingNightly] = useAuthenticatedMutation({
        mutation: CREATE_BOOKING_NIGHTLY,
        onCompleted: (data) => {
            setOrderDataDaily(data)
            setBookingId(data.create_night_parking_booking.id)
            setSuccessfulBooking(data.create_night_parking_booking.success)
            // removing duplicates
            const unavailableDatesFromApi: (string | null)[] =
                data?.create_night_parking_booking?.unavailable_dates ?? []
            setUnavailableDates([...new Set(unavailableDatesFromApi as string[])])
            setReferenceNumber(data?.create_night_parking_booking?.booking_reference ?? '')
        },
        onError: () => {
            // TODO: handle or return error
        }
    })

    const [createBookingDaily] = useMutation(CREATE_BOOKING_DAILY, {
        onCompleted: (data) => {
            setOrderDataDaily(data)
            setBookingId(data.booking_create_booking.id)
            setSuccessfulBooking(data.booking_create_booking.success)
            // removing duplicates
            const unavailableDatesFromApi: (string | null)[] = data?.booking_create_booking?.unavailable_dates ?? []
            setUnavailableDates([...new Set(unavailableDatesFromApi as string[])])
            setReferenceNumber(data?.booking_create_booking?.booking_reference ?? '')
        },
        onError: (error) => {
            // TODO: handle or return error
        }
    })

    const [createBookingHourly] = useMutation(CREATE_BOOKING_HOURLY, {
        onCompleted: (data) => {
            setOrderDataHourly(data)
            setReferenceNumber(data?.booking_create_booking_hourly?.booking_reference ?? '')
            setBookingId(data.booking_create_booking_hourly.id)
            setSuccessfulBooking(data.booking_create_booking_hourly.success)
        },
        onError: (error) => {
            // TODO: handle or return error
        }
    })

    const [createBookingNightly] = useMutation(CREATE_BOOKING_NIGHTLY, {
        onCompleted: (data) => {
            setOrderDataDaily(data)
            setBookingId(data.create_night_parking_booking.id)
            setSuccessfulBooking(data.create_night_parking_booking.success)
            // removing duplicates
            const unavailableDatesFromApi: (string | null)[] =
                data?.create_night_parking_booking?.unavailable_dates ?? []
            setUnavailableDates([...new Set(unavailableDatesFromApi as string[])])
            setReferenceNumber(data?.create_night_parking_booking?.booking_reference ?? '')
        },
        onError: (error) => {
            // TODO: handle or return error
        }
    })

    const createBookingMapper = () => {
        if (isLoggedIn) {
            if (checkout?.lotCheckoutType === LotViewName.HOURLY) {
                return createUserBookingHourly
            }
            if (checkout?.lotCheckoutType === LotViewName.DAILY) {
                return createUserBookingDaily
            }
            if (checkout?.lotCheckoutType === LotViewName.NIGHTLY) {
                return createUserBookingNightly
            }
            if (checkout?.lotCheckoutType === LotViewName.SUBSCRIPTION) {
                return createUserBookingDaily
            }
        } else {
            if (checkout?.lotCheckoutType === LotViewName.HOURLY) {
                return createBookingHourly
            }
            if (checkout?.lotCheckoutType === LotViewName.DAILY) {
                return createBookingDaily
            }
            if (checkout?.lotCheckoutType === LotViewName.NIGHTLY) {
                return createBookingNightly
            }
        }
    }

    const createBooking = createBookingMapper()

    return {
        successfulBooking,
        bookingId,
        orderDataDaily,
        orderDataHourly,
        unavailableDates,
        lot,
        referenceNumber,
        checkout,
        createBooking,
        setBookingId,
        setSuccessfulBooking,
        modifyAndSetCheckout,
        setOrderDataDaily,
        setOrderDataHourly,
        clearAllBookingData,
        onBookingError
    }
}
